import {
  FilterDTO,
  FilterValueDTO,
  IScoresOfWellDropsDTO,
  ISearchDisplayFieldDTO,
  Pagination,
  SearchCombinator,
  SearchConstraintRootDTO,
  SearchDTO,
  SearchDisplayFieldDTO,
  SearchNodeDTO,
  SearchRequestDTO,
  SearchTargetDTO,
  WellIngDTO,
} from 'business/models/Models';
import { cloneDeep } from 'lodash';
import { useCallback, useMemo } from 'react';
import { RuleGroupType, RuleGroupTypeIC, convertFromIC } from 'react-querybuilder';
import { useDispatch } from 'react-redux';
import { useRMSelector } from 'redux/hooks';
import {
  SearchDetailsState,
  SearchResultState,
  setDisplayFields,
  discardSearchDetails,
  setCurrentPage,
  setSelectedScores,
  clearSearchFilters,
  clearSearchResult,
  setConstraints,
  setSearchName,
  setSearchPaths,
  setSelectedTargetId,
  clearChartsData,
  toggleHasIndependentConstraints,
} from 'redux/reducers/searchnFilterReducer';
import {
  fetchScoresAction,
  fetchSearchDetailsAction,
  fetchSearchTargets,
  saveSearchAction,
  searchFiltersThunk,
  searchResultThunk,
  defaultSelectedSearchFieldsThunk,
  searchResultFilterValuesThunk,
  searchRowsCountThunk,
  searchChartDataThunk,
} from 'redux/thunks/searchnFilter';
import { getRulesModelArray, getSearchConstraintModel } from 'transformers/searchnFilter';
import { getModelArray, getModelObject } from 'transformers/utils';
import { useDefaultSelectedSearchFields } from './common';
import { fetchDrawingPreferences } from 'redux/thunks/well';
import { DropDetails } from 'components/well/constants';
import {
  FilterDataDTO,
  FiltersState,
} from 'components/sidePanel/searchAndFilter/constants';
import { checkAllRulesFromNonIc } from 'components/querybuilder';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { TreeService } from 'business/services/TreeService';
import SearchnFilterService from 'business/services/searchnFilterService';
import { searchTargetChartsThunk } from 'redux/thunks/common';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import { useModalHandlers } from 'components/modal';
export const useFetchSearchTargets = () => {
  const dispatch = useDispatch();
  return () => dispatch(fetchSearchTargets());
};

export const useSearchDetailsDisacrd = () => {
  const dispatch = useDispatch();
  return () => dispatch(discardSearchDetails({}));
};

export const useSetSelectedSearchTarget = () => {
  const defaultSelectedSearchFields = useDefaultSelectedSearchFields();
  const searchConstraints = useSearchConstraints();
  const dispatch = useDispatch();
  return (targetId: number) => {
    dispatch(setSelectedTargetId(targetId));
    const searchFields = defaultSelectedSearchFields[targetId];
    const displayFields = searchFields.map(
      (sf, i) =>
        ({
          searchFieldId: sf.id,
          columnHeader: sf.fieldAndTableDisplayName,
          status: sf.status,
          displayOrder: i + 1,
        } as ISearchDisplayFieldDTO),
    );
    dispatch(setDisplayFields(displayFields));
    dispatch(setConstraints({ ...searchConstraints, rules: [] }));
    dispatch(clearSearchFilters());
    dispatch(clearSearchResult());
  };
};

export const useSetSearchName = () => {
  const dispatch = useDispatch();
  return (name: string) => dispatch(setSearchName(name));
};

export const useSearchName = (): string => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.searchName);
};

export const useSearchPaths = (): number[] => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.searchPaths);
};

export const useSetSearchPaths = () => {
  const dispatch = useDispatch();
  return (ids: number[]) => dispatch(setSearchPaths(ids));
};

export const useSetSearchConstraints = () => {
  const dispatch = useDispatch();
  return (constraints: RuleGroupType) => dispatch(setConstraints(constraints));
};

export const useSearchConstraints = (): RuleGroupType => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.constraints);
};

export const useSelectedSearchTarget = (): number => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.selectedTargetId);
};

export const useSearchTargets = (): SearchTargetDTO[] => {
  return useRMSelector((state) => state.searchnFilter.searchTargets.data);
};

export const useSearchTargetRelations = (): { [key: string]: SearchTargetDTO[] } => {
  return useRMSelector((state) => state.searchnFilter.targetRelations.data);
};

export const useFetchSearchDetails = () => {
  const dispatch = useDispatch();
  return (id: number) => dispatch(fetchSearchDetailsAction(id));
};

export const useIsFetchingSearchDetails = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.isFetching);
};

export const useWellDropsWithIngredients = (): WellIngDTO[] => {
  return useRMSelector((state) => state.searchnFilter.wellDrops.data.items);
};

export const useIsFetchingWellDrops = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.wellDrops.isFetching);
};

export const useSearchResult = (): SearchResultState => {
  return useRMSelector((state) => state.searchnFilter.searchResult);
};

export const useSearchDetails = (): SearchDetailsState => {
  return useRMSelector((state) => state.searchnFilter.searchDetails);
};

export const useSearchDetailsOriginal = (): SearchDetailsState => {
  return useRMSelector((state) => state.searchnFilter.searchDetailsOriginal);
};

export const useSearchSelectedRows = (): { [key: string]: any }[] => {
  const searchResult = useRMSelector((state) => state.searchnFilter.searchResult);
  const selectedRows = searchResult.selectedRowIndices.map((idx) => {
    const newIndex =
      idx +
      searchResult.rows.pagination.perPage * (searchResult.rows.pagination.page - 1);
    return searchResult.rows.items[newIndex];
  });
  return selectedRows;
};

export interface IWellDropsData {
  wellVolume: number;
  dropProteinVolume: number;
  dropNumber: number;
  seedId: number | null;
}

export const useLatestSelectedSearchRow = (): { [key: string]: any } => {
  const selectedRowIndices = useRMSelector(
    (state) => state.searchnFilter.searchResult.selectedRowIndices,
  );
  const items = useRMSelector((state) => state.searchnFilter.searchResult.rows.items);
  return items[selectedRowIndices[selectedRowIndices.length - 1]];
};

export const useSelectedWells = () => {
  return useRMSelector((state) => state.searchnFilter.canvasWellSelection);
};

export const useScores = () => {
  return useRMSelector((state) => state.searchnFilter.scores);
};

export const usePerformSearch = () => {
  const searchResult = useSearchResult();
  const searchDetails = useSearchDetails();
  const isWellDropOrXtalSearch = useIsWellDropOrXtalSearch();
  const selectedChartId = useSelectedChartId();
  const getChartData = useGetSearchChartData();
  const { perPage } = searchResult.rows.pagination;
  const dispatch = useDispatch();
  const { id, displayFields, constraints, selectedTargetId, searchPaths, filters } =
    searchDetails;
  const nonIcConstraints = constraints.combinator
    ? constraints
    : convertFromIC(constraints as RuleGroupTypeIC);
  return (pagination: Pagination = undefined, persistInRedux = true) => {
    // check if constraints are valid and there is no null/empty rule(except not null and null comparison operators)
    if (!checkAllRulesFromNonIc(nonIcConstraints)) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'There are some null/empty rules, either remove them or enter some valid value',
      );
      return false;
    }
    const filteredFilters = filters.enableFilters
      ? filters.data
          .filter((f) => f.selectedValues?.length)
          .map(
            ({ allValues, selectedValues, ...rest }) =>
              new FilterDTO({
                ...rest,
                values: rest.values.map((v) => new FilterValueDTO({ ...v })),
              }),
          )
      : [];
    const hitScores = searchDetails.screenHitSelectedScore
      ? Object.values(searchDetails.screenHitSelectedScore).flat()
      : [];
    const searchRequest = {
      id: id,
      pagination: pagination || new Pagination({ page: 1, perPage }),
      selectedHitScoreIds: hitScores,
      searchPathTreeNodeIds: searchPaths,
      searchTargetId: selectedTargetId,
      selectDistinct: searchDetails.selectDistinct,
      displayFields: getModelArray(displayFields, SearchDisplayFieldDTO),
      constraints: getSearchConstraintModel(nonIcConstraints),
      rootTreeNodeId: searchDetails.rootTreeNodeId,
      filters: filteredFilters,
      countOnly: false,
      isWellDropSearch: isWellDropOrXtalSearch,
      hasIndependentConstraints: searchDetails.hasIndependentConstraints,
    };
    if (persistInRedux) {
      dispatch(clearChartsData());
      dispatch(searchResultThunk(searchRequest));
      getChartData(selectedChartId);
      if (!pagination) {
        dispatch(setCurrentPage(1));
        dispatch(searchRowsCountThunk({ ...searchRequest, countOnly: true }));
      }
    } else {
      const dataService = new SearchnFilterService();
      const paramsModel: SearchRequestDTO = getModelObject(
        searchRequest,
        SearchRequestDTO,
      );
      return dataService.PerformSearch(paramsModel);
    }
  };
};

export const useSearchDisplayFields = (): ISearchDisplayFieldDTO[] => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.displayFields);
};

export const useCurrentSearchId = (): number => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.id);
};

export const useIsSearchInProgress = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.searchResult.isFetching);
};
export const useIsSavingSearch = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.isSavingSearch);
};

export const useIsDirtySearchDetails = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.isDirty);
};

export const useSaveSearch = () => {
  const dispatch = useDispatch();
  const searchDetails = useSearchDetails();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const showFileNotFoundModal = useFileNotFoundModal();
  const { showModal, hideModal } = useModalHandlers();

  return useCallback(() => {
    if (searchDetails.isSavingSearch) {
      return;
    }
    if (!searchDetails.searchName || searchDetails.searchName.trim() === '') {
      showModal({
        isOkOnly: true,
        onOk: hideModal,
        body: 'Search And Filter name cannot be empty.',
        heading: 'Warning',
        okButtonText: 'Ok',
      });
      return;
    }
    const {
      displayFields,
      id,
      searchName,
      selectedTargetId,
      constraints,
      searchPaths,
      selectDistinct,
      filters,
      searchNode,
      selectedChartId,
      hasIndependentConstraints,
    } = searchDetails;
    //non-independent-combinator
    const nonIcConstraints = constraints.combinator
      ? constraints
      : convertFromIC(constraints as RuleGroupTypeIC);
    const filteredFilters = filters.data.map(
      ({ allValues, selectedValues, ...rest }) =>
        new FilterDTO({
          ...rest,
          values: rest.values.map((v) => new FilterValueDTO({ ...v })),
        }),
    );
    const searchModel: SearchDTO = new SearchDTO({
      id,
      node: getModelObject({ name: searchName }, SearchNodeDTO),
      searchTargetId: selectedTargetId,
      constraints: new SearchConstraintRootDTO({
        combinator: nonIcConstraints.combinator as SearchCombinator,
        rules: getRulesModelArray(cloneDeep(nonIcConstraints.rules)),
        not: nonIcConstraints.not,
      }),
      searchPathTreeNodeIds: searchPaths,
      displayFields: getModelArray(displayFields, SearchDisplayFieldDTO),
      selectDistinct,
      filters: filteredFilters,
      selectedChartId,
      hasIndependentConstraints,
    });
    try {
      dispatch(
        saveSearchAction({
          searchDto: searchModel,
          onFullfill: () =>
            notifyExplorerforSNFNameChange(
              searchNode.id,
              searchName,
              explorerBroadcastChannel,
            ),
          onError: (error) => {
            if (error.StatusCode === 404) {
              showFileNotFoundModal();
            }
          },
        }),
      );
    } catch (error) {
      if (error.StatusCode === 404) {
        showFileNotFoundModal();
      }
    }
  }, [searchDetails]);
};

export const notifyExplorerforSNFNameChange = async (
  searchTreeNodeId,
  searchName,
  explorerBroadcastChannel,
) => {
  const treeService = new TreeService();
  const treeNode = await treeService.getTreeNodeById(searchTreeNodeId);
  treeNode.name = searchName;
  explorerBroadcastChannel.postMessage({
    action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
    payload: treeNode,
  });
};

export const useFetchScores = () => {
  const dispatch = useDispatch();
  return (dropIds: number[]) => dispatch(fetchScoresAction(dropIds));
};

export const useFetchSearchTargetFieldsMap = () => {
  const dispatch = useDispatch();
  return () => dispatch(defaultSelectedSearchFieldsThunk());
};

export const useFetchSearchFilters = () => {
  const dispatch = useDispatch();
  return (searchId: number) => dispatch(searchFiltersThunk(searchId));
};

export const useSearchResultFilterValues = (fieldId: number) => {
  const searchDetails = useSearchDetails();
  const dispatch = useDispatch();
  const { id, displayFields, constraints, selectedTargetId, searchPaths } = searchDetails;
  const nonIcConstraints = constraints.combinator
    ? constraints
    : convertFromIC(constraints as RuleGroupTypeIC);
  return () => {
    const hitScores = searchDetails.screenHitSelectedScore
      ? Object.values(searchDetails.screenHitSelectedScore).flat()
      : [];
    dispatch(
      searchResultFilterValuesThunk({
        id: id,
        selectedHitScoreIds: hitScores,
        searchPathTreeNodeIds: searchPaths,
        searchTargetId: selectedTargetId,
        selectDistinct: searchDetails.selectDistinct,
        displayFields: getModelArray(displayFields, SearchDisplayFieldDTO),
        constraints: getSearchConstraintModel(nonIcConstraints),
        rootTreeNodeId: searchDetails.rootTreeNodeId,
        searchFieldId: fieldId,
        hasIndependentConstraints: searchDetails.hasIndependentConstraints,
      }),
    );
  };
};

export const useSearchFilterOriginalValues = (filterId: number) => {
  const filters = useRMSelector(
    (state) => state.searchnFilter.searchDetails.filters.data,
  );
  const filterIndex = filters.findIndex((d) => d.id === filterId);
  if (filterIndex !== -1) {
    return filters[filterIndex]?.originalValues;
  }
};

export const useSearchTargetFields = (): SearchTargetDTO[] => {
  const searchTargetId = useSelectedSearchTarget();
  const targetRelations = useSearchTargetRelations();
  return targetRelations[searchTargetId];
};

export const useFetchDrawingPreferences = () => {
  const dispatch = useDispatch();
  return () => dispatch(fetchDrawingPreferences(1));
};

export const useSelectedWellDropDetails = (): DropDetails[] => {
  const { items, selectedRowIndices } = useRMSelector((state) => ({
    items: state.searchnFilter.searchResult.rows.items,
    selectedRowIndices: state.searchnFilter.searchResult.selectedRowIndices,
    wellDrops: state.searchnFilter.wellDrops.data.items,
  }));

  return useMemo(() => {
    if (!items.length) {
      return [];
    }

    return selectedRowIndices
      .map((index) => {
        const selectedRow = items[index];

        if (!selectedRow) {
          return null;
        }

        return {
          expId: selectedRow['Experiment.ID'],
          plateID: selectedRow['Experiment Plate.ID'],
          wellNum: selectedRow['Well.Number'],
          dropNumber: selectedRow['Well Drop.Number'],
          dropId: selectedRow['Well Drop.ID'],
          cpID: selectedRow['Capture Result.Profile ID'],
          imageBatchId: selectedRow['Capture Result.Image Batch ID'],
          regionID: selectedRow['Region.ID'],
          rowNumber: selectedRow['RowNumber'],
        } as DropDetails;
      })
      .filter((x): x is DropDetails => x !== null);
  }, [selectedRowIndices]);
};

export const useSearchFilters = (): FiltersState => {
  return useRMSelector((state) => state?.searchnFilter?.searchDetails?.filters);
};

export const useSelectedSearchFilter = (id: number): FilterDataDTO => {
  return useRMSelector(
    (state) => state?.searchnFilter?.searchDetails?.filters.data,
  )?.find((f) => f.id === id);
};

export const useWellScores = (
  dropId: number,
  batchId: number,
): IScoresOfWellDropsDTO[] => {
  return useRMSelector(
    (state) => state.searchnFilter.scores.scores[`${dropId}-${batchId}`] ?? [],
  );
};

export const useSelectedWellDropDetailsIndex = () => {
  return useRMSelector((state) => state.well.selectedWellDropDetailsIndex);
};
export const useSelectedScores = (groupId: number) => {
  return useRMSelector(
    (state) => state.searchnFilter.searchDetails.screenHitSelectedScore[groupId],
  );
};
export const useAllSelectedScores = () => {
  return useRMSelector(
    (state) => state.searchnFilter.searchDetails.screenHitSelectedScore,
  );
};
export const useSetSelectedScores = () => {
  const dispatch = useDispatch();
  return (selectedScores: { [groupId: number]: number[] }) =>
    dispatch(setSelectedScores(selectedScores));
};

export const useIsSelectedSearchTargetScreenHits = (): boolean => {
  const selectedSearchTargetId: number = useSelectedSearchTarget();
  const searchTargets = useSearchTargets();
  return useMemo(
    () =>
      searchTargets.find((item) => item.id === selectedSearchTargetId)?.name ==
      'Screen Hits',
    [searchTargets, selectedSearchTargetId],
  );
};

export const useAllowEditSearch = (): boolean => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.allowEdits);
};

const dropSearchTargets = ['Well Drop', 'Drops by Score', 'Xtals V4'];
export const useIsWellDropOrXtalSearch = (): boolean => {
  const selectedTargetId = useSelectedSearchTarget();
  const targets = useSearchTargets();
  const isWellDropSearch = useMemo(
    () =>
      targets.some(
        (t) => t.id === selectedTargetId && dropSearchTargets.includes(t.name),
      ),
    [selectedTargetId, targets],
  );
  return isWellDropSearch;
};

export const useCurrentSelectedWellDropDetails = () => {
  const allSelectedWellDropDetails = useSelectedWellDropDetails();
  const selectedWellDropDetailsIndex = useSelectedWellDropDetailsIndex();
  if (!allSelectedWellDropDetails.length) {
    return null;
  }
  return allSelectedWellDropDetails?.[selectedWellDropDetailsIndex];
};

export const useSearchSelectedRowsIndices = () => {
  return useRMSelector((state) => state.searchnFilter.searchResult.selectedRowIndices);
};

export const useSelectedSearchTargetName = () => {
  return useRMSelector(
    (state) =>
      state.searchnFilter.searchTargets.data.find(
        (item) => item.id === state.searchnFilter.searchDetails.selectedTargetId,
      )?.name,
  );
};

export const useIsReportsDisabled = (isResultTab, isDropTab, isCompareTab) => {
  const selectedSearchTargetType = useSelectedSearchTargetName();
  return !(
    (isResultTab || isDropTab || isCompareTab) &&
    (selectedSearchTargetType == 'Experiment' ||
      selectedSearchTargetType == 'Well Drop' ||
      selectedSearchTargetType == 'Drops by Score')
  );
};

export const useGetSearchChartData = () => {
  const searchDetails = useSearchDetails();
  const dispatch = useDispatch();
  const { id, displayFields, constraints, selectedTargetId, searchPaths, filters } =
    searchDetails;
  const nonIcConstraints = constraints.combinator
    ? constraints
    : convertFromIC(constraints as RuleGroupTypeIC);

  const filteredFilters = filters.enableFilters
    ? filters.data
        .filter((f) => f.selectedValues?.length)
        .map(
          ({ allValues, selectedValues, ...rest }) =>
            new FilterDTO({
              ...rest,
              values: rest.values.map((v) => new FilterValueDTO({ ...v })),
            }),
        )
    : [];

  return (chartId: number) => {
    if (chartId > 0) {
      const hitScores = searchDetails.screenHitSelectedScore
        ? Object.values(searchDetails.screenHitSelectedScore).flat()
        : [];
      dispatch(
        searchChartDataThunk({
          id: id,
          selectedHitScoreIds: hitScores,
          searchPathTreeNodeIds: searchPaths,
          searchTargetId: selectedTargetId,
          selectDistinct: searchDetails.selectDistinct,
          displayFields: getModelArray(displayFields, SearchDisplayFieldDTO),
          constraints: getSearchConstraintModel(nonIcConstraints),
          rootTreeNodeId: searchDetails.rootTreeNodeId,
          chartId: chartId,
          filters: filteredFilters,
          hasIndependentConstraints: searchDetails.hasIndependentConstraints,
        }),
      );
    }
  };
};
export const useSelectChartData = () => {
  return useRMSelector((state) => state.searchnFilter.charts);
};

export const useFetchSearchTargetCharts = () => {
  const dispatch = useDispatch();
  return () => dispatch(searchTargetChartsThunk());
};

export const useGetSelectedTargetCharts = () => {
  const selectedTargetId: number = useSelectedSearchTarget();
  return useRMSelector((state) => state.common.searchTargetCharts[selectedTargetId]);
};

export const useSelectedChartId = () => {
  return useRMSelector((state) => state.searchnFilter.searchDetails.selectedChartId);
};

export const useSelectedChartName = () => {
  const charts = useGetSelectedTargetCharts();
  const selectedChartId = useSelectedChartId();
  return charts?.find((c) => c.id === selectedChartId)?.title;
};

export const useCurrentPageSearchResults = () => {
  return useRMSelector((state) => state.searchnFilter.searchResult.rows.items);
};

export const useHasIndependentConstraints = () => {
  const dispatch = useDispatch();
  const hasIndependentConstraints = useRMSelector(
    (state) => state.searchnFilter.searchDetails.hasIndependentConstraints,
  );

  const toggleHasIC = () => dispatch(toggleHasIndependentConstraints());

  return [hasIndependentConstraints, toggleHasIC] as const;
};
